import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  TableCellLayout,
  Spinner,
  MessageBarBody,
  makeStyles,
  tokens
} from '@fluentui/react-components';
import { Add20Regular, Delete24Regular, Edit24Regular, Tag24Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../redux/reducers';
import { ServiceCategory } from '../../../models/Entities/ServiceCategory';
import ServiceCategoryFormComponent from './ServiceCategory.Form.Component';
import ConfirmationModalComponent from '../../../commons/component/ConfirmationModal/ConformationModal.Component';

// i18Next
import { useTranslation } from 'react-i18next';
import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// store
import * as ServiceCategoryStore from '../../../redux/middleware/ServiceCategoryMiddleware';

export interface IProps {
  isOpen: boolean;
  onDismiss: () => void;
}

export const ServiceCategoryListComponent = (props: IProps) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const styles = useStyles();

  const ServiceCategoryState = useSelector((appState: AppState) => appState.ServiceCategories);

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | undefined>();

  useEffect(() => {
    if (!ServiceCategoryState?.successLoadingAll && !ServiceCategoryState?.isLoadingAll) {
      dispatch(ServiceCategoryStore.actionCreators.GetAllServiceCategories());
    }
  }, []);

  // Reload list after successful add, update or delete
  useEffect(() => {
    if (
      ServiceCategoryState?.isAddingSuccess ||
      ServiceCategoryState?.isSavingSuccess ||
      ServiceCategoryState?.isDeletingSuccess
    ) {
      dispatch(ServiceCategoryStore.actionCreators.GetAllServiceCategories());
    }
  }, [
    ServiceCategoryState?.isAddingSuccess,
    ServiceCategoryState?.isSavingSuccess,
    ServiceCategoryState?.isDeletingSuccess
  ]);

  const handleAddNew = () => {
    setSelectedCategory(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (category: ServiceCategory) => {
    setSelectedCategory(category);
    setIsFormOpen(true);
  };

  const handleFormDismiss = () => {
    setIsFormOpen(false);
    setSelectedCategory(undefined);
  };

  const categories = ServiceCategoryState?.list || [];

  return (
    <>
      <GuideDrawer
        open={props.isOpen}
        size="large"
        title={t('serviceCategoryList.title')}
        onDismiss={props.onDismiss}
        testId="service-category-list-drawer"
        primaryAction={{ label: t('serviceCategoryList.addButton'), onClick: handleAddNew, icon: <Add20Regular /> }}
        secondaryAction={{ label: t('guideDrawer.close') }}
      >
        {ServiceCategoryState?.isLoadingAll && (
          <div className={styles.loadingContainer}>
            <Spinner label={t('serviceCategoryList.loading')} />
          </div>
        )}

        {ServiceCategoryState?.failOnLoadingAll && ServiceCategoryState?.error && (
          <GuideMessageBar intent="error">
            <MessageBarBody>
              {ServiceCategoryState.error.ErrorMessage}
            </MessageBarBody>
          </GuideMessageBar>
        )}

        {ServiceCategoryState?.isDeleting && (
          <GuideMessageBar intent="info">
            <MessageBarBody>
              {t('serviceCategoryList.deleting')}
            </MessageBarBody>
          </GuideMessageBar>
        )}

        {ServiceCategoryState?.isDeletingSuccess && (
          <GuideMessageBar intent="success">
            <MessageBarBody>
              {t('serviceCategoryList.deleteSuccess')}
            </MessageBarBody>
          </GuideMessageBar>
        )}
        {ServiceCategoryState?.isAddingSuccess && (
          <GuideMessageBar intent="success">
            <MessageBarBody>
              {t('serviceCategoryList.addSuccess')}
            </MessageBarBody>
          </GuideMessageBar>
        )}

        {ServiceCategoryState?.isSavingSuccess && (
          <GuideMessageBar intent="success">
            <MessageBarBody>
              {t('serviceCategoryList.updateSuccess')}
            </MessageBarBody>
          </GuideMessageBar>
        )}
        {categories.length > 0 && (
          <Table aria-label="Service Categories">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('serviceCategoryList.columnDescription')}</TableHeaderCell>
                <TableHeaderCell>{t('serviceCategoryList.columnActions')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((category: ServiceCategory) => (
                <TableRow key={category.id}>
                  <TableCell>
                    <TableCellLayout media={<Tag24Regular />}>
                      {category.description || category.id}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout>
                      <div className={styles.rowActions}>
                        <Button
                          appearance="subtle"
                          icon={<Edit24Regular />}
                          onClick={() => handleEdit(category)}
                          disabled={ServiceCategoryState?.isDeleting}
                        >
                          {t('serviceCategoryList.editButton')}
                        </Button>
                        <ConfirmationModalComponent
                          onDismiss={() => {}}
                          onConfirm={() => dispatch(ServiceCategoryStore.actionCreators.DeleteServiceCategory(category.id))}
                          title={t('serviceCategoryList.deleteButton')}
                          message={t('serviceCategoryList.confirmDelete')}
                          buttonApparence="subtle"
                          buttonShape={undefined}
                          buttonTitle={t('serviceCategoryList.deleteButton')}
                          buttonIcon={<Delete24Regular />}
                          isProcessing={ServiceCategoryState?.isDeleting}
                        />
                      </div>
                    </TableCellLayout>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!ServiceCategoryState?.isLoadingAll && categories.length === 0 && (
          <GuideMessageBar intent="info">
            <MessageBarBody>
              {t('serviceCategoryList.noCategories')}
            </MessageBarBody>
          </GuideMessageBar>
        )}
      </GuideDrawer>

      {isFormOpen && (
        <ServiceCategoryFormComponent
          isOpen={isFormOpen}
          dismissPanel={handleFormDismiss}
          ServiceCategory={selectedCategory}
        />
      )}
    </>
  );
};

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacingVerticalXL
  },
  rowActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS
  }
});

export default ServiceCategoryListComponent;
